import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, LogOut, Mail, ShieldAlert, Flame, Sparkles, MessageSquare, Heart } from "lucide-react";
import { api, fullPrice } from "../lib/api";
import { useAuth } from "../lib/auth";
import PropertyForm from "../components/PropertyForm";
import Seo from "../components/Seo";
import NotificationsBell from "../components/NotificationsBell";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("properties");
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loanApps, setLoanApps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [transcriptOpen, setTranscriptOpen] = useState(null); // { lead, messages }
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const effectiveUser = user || location.state?.user;

  const loadProperties = async () => {
    try {
      const { data } = await api.get("/admin/properties", { withCredentials: true });
      setProperties(data);
    } catch (e) {
      toast.error("Failed to load properties");
    }
  };

  const loadInquiries = async () => {
    try {
      const { data } = await api.get("/admin/inquiries", { withCredentials: true });
      setInquiries(data);
    } catch {
      toast.error("Failed to load inquiries");
    }
  };

  const loadLoanApps = async () => {
    try {
      const { data } = await api.get("/admin/loan-applications", { withCredentials: true });
      setLoanApps(data);
    } catch {
      toast.error("Failed to load loan applications");
    }
  };

  const loadLeads = async () => {
    try {
      const { data } = await api.get("/admin/leads", { withCredentials: true });
      setLeads(data);
    } catch {
      toast.error("Failed to load leads");
    }
  };

  const openTranscript = async (lead) => {
    try {
      const { data } = await api.get(`/admin/leads/${lead.session_id}/transcript`, { withCredentials: true });
      setTranscriptOpen({ lead, messages: data });
    } catch {
      toast.error("Failed to load transcript");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name || lead.session_id}"?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`, { withCredentials: true });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  useEffect(() => {
    if (effectiveUser?.is_admin) {
      loadProperties();
      loadInquiries();
      loadLoanApps();
      loadLeads();
    }
  }, [effectiveUser]);

  if (loading) return <div className="py-32 text-center text-gray-500">Loading…</div>;
  if (!effectiveUser) return <Navigate to="/admin/login" replace />;
  if (!effectiveUser.is_admin) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-md bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <ShieldAlert className="w-10 h-10 text-[#E63946] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-semibold">Not authorized</h1>
          <p className="text-sm text-gray-500 mt-3">
            The account <span className="font-medium">{effectiveUser.email}</span> is not in the admin whitelist.
          </p>
          <button
            onClick={logout}
            className="mt-6 bg-[#1A1A1A] text-white px-5 py-2.5 rounded-md text-sm font-semibold"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const onSave = async (payload, id) => {
    try {
      if (id) {
        await api.put(`/admin/properties/${id}`, payload, { withCredentials: true });
        toast.success("Property updated");
      } else {
        await api.post("/admin/properties", payload, { withCredentials: true });
        toast.success("Property created");
      }
      setFormOpen(false);
      setEditing(null);
      await loadProperties();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`, { withCredentials: true });
      toast.success("Property deleted");
      setProperties((list) => list.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" noindex path="/admin" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-2">
            Admin Dashboard
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter">
            Hello, {effectiveUser.name?.split(" ")[0] || "Admin"}.
          </h1>
          <div className="text-sm text-gray-500 mt-2">{effectiveUser.email}</div>
        </div>
        <div className="flex items-center gap-3">
          <NotificationsBell />
          <Link to="/" className="text-sm text-gray-600 hover:text-[#E63946]">← Back to site</Link>
          <Link
            to="/crm"
            className="flex items-center gap-2 bg-[#1A1A1A] text-white hover:bg-[#333] text-sm px-4 py-2 rounded-md font-semibold"
            data-testid="admin-open-crm"
          >
            <Sparkles className="w-4 h-4" /> Open CRM
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#E63946] text-sm px-4 py-2 rounded-md"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 border-b border-gray-200">
        <div className="flex gap-6">
          {[
            { key: "properties", label: `Properties (${properties.length})` },
            { key: "leads", label: `AI Leads (${leads.length})` },
            { key: "inquiries", label: `Inquiries (${inquiries.length})` },
            { key: "loan_apps", label: `Loan Applications (${loanApps.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#E63946] text-[#E63946]"
                  : "border-transparent text-gray-500 hover:text-[#1A1A1A]"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {tab === "properties" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-semibold">All listings</h2>
              <button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2.5 rounded-md font-semibold text-sm flex items-center gap-2"
                data-testid="admin-new-property"
              >
                <Plus className="w-4 h-4" /> New Listing
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-[10px] uppercase tracking-[0.18em] text-gray-500">
                  <tr>
                    <th className="text-left px-5 py-3">Property</th>
                    <th className="text-left px-5 py-3">Neighborhood</th>
                    <th className="text-left px-5 py-3">Price</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Featured</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-stone-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt="" className="w-12 h-12 rounded object-cover bg-gray-100" />
                          <div>
                            <div className="font-medium text-[#1A1A1A]">{p.title}</div>
                            <div className="text-xs text-gray-500">{p.address}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-700">{p.neighborhood}</td>
                      <td className="px-5 py-3 font-semibold text-[#1A1A1A]">{fullPrice(p.price)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-block text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${
                          p.status === "For Sale" ? "bg-green-100 text-green-900"
                          : p.status === "Pending" ? "bg-amber-100 text-amber-900"
                          : "bg-gray-200 text-gray-700"
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-5 py-3 text-gray-700">{p.featured ? "★" : "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => { setEditing(p); setFormOpen(true); }}
                            className="p-1.5 text-gray-600 hover:text-[#E63946]"
                            data-testid={`admin-edit-${p.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDelete(p)}
                            className="p-1.5 text-gray-600 hover:text-red-600"
                            data-testid={`admin-delete-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {properties.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-gray-500 py-12">No listings yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "inquiries" && (
          <>
            <h2 className="font-display text-2xl font-semibold mb-6">Customer inquiries</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inquiries.map((q) => (
                <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-lg font-semibold">{q.name}</div>
                      <div className="text-sm text-gray-500">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
                    </div>
                    <a href={`mailto:${q.email}`} className="text-[#E63946] hover:text-[#c9313d]">
                      <Mail className="w-4 h-4" />
                    </a>
                  </div>
                  <p className="mt-4 text-sm text-[#333] leading-relaxed whitespace-pre-line">{q.message}</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    {q.property_id ? (
                      <Link to={`/properties/${q.property_id}`} className="hover:text-[#E63946]">
                        Re: {q.property_id}
                      </Link>
                    ) : <span>General inquiry</span>}
                    <span>{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
              {inquiries.length === 0 && (
                <div className="col-span-full text-center text-gray-500 py-12">No inquiries yet.</div>
              )}
            </div>
          </>
        )}
        {tab === "leads" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#E63946]" /> AI-Qualified Leads
                </h2>
                <p className="text-sm text-gray-500 mt-1">Ava auto-extracts buyer/seller/investor details from every chat. Sorted by lead score.</p>
              </div>
              <button onClick={loadLeads} className="text-xs uppercase tracking-[0.15em] text-gray-500 hover:text-[#E63946]" data-testid="admin-leads-refresh">Refresh</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {leads.map((l) => {
                const tierColors = {
                  hot: "bg-red-50 text-red-700 border-red-200",
                  warm: "bg-amber-50 text-amber-700 border-amber-200",
                  cold: "bg-gray-50 text-gray-600 border-gray-200",
                };
                return (
                  <div key={l.id} className="bg-white border border-gray-200 rounded-xl p-6" data-testid={`admin-lead-${l.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-display text-lg font-semibold">{l.name || "Anonymous visitor"}</div>
                          {l.tier === "hot" && <Flame className="w-4 h-4 text-red-500" />}
                        </div>
                        <div className="text-sm text-gray-500">
                          {l.email || "—"}{l.phone ? ` · ${l.phone}` : ""}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border ${tierColors[l.tier] || tierColors.cold}`}>
                          {l.tier} · {l.score}
                        </span>
                        {l.saved_count >= 3 && (
                          <span
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-[#E63946] text-white flex items-center gap-1"
                            data-testid={`admin-lead-hot-saves-${l.id}`}
                            title="High-intent — has saved 3+ homes"
                          >
                            <Heart className="w-3 h-3" fill="white" strokeWidth={0} />
                            {l.saved_count} saved
                          </span>
                        )}
                      </div>
                    </div>

                    {l.summary && (
                      <p className="mt-3 text-sm text-[#444] italic leading-relaxed">&ldquo;{l.summary}&rdquo;</p>
                    )}

                    {l.saved_listings?.length > 0 && (
                      <div className="mt-4 bg-red-50/60 border border-red-100 rounded-lg p-3" data-testid={`admin-lead-saved-block-${l.id}`}>
                        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.18em] text-red-700 font-semibold">
                          <Heart className="w-3 h-3" fill="#b91c1c" strokeWidth={0} />
                          Saved homes ({l.saved_count})
                        </div>
                        <ul className="space-y-1 text-xs text-[#1A1A1A]">
                          {l.saved_listings.map((s) => (
                            <li key={s.listing_key} className="flex items-center justify-between gap-3">
                              <span className="truncate">{s.address}{s.city ? `, ${s.city}` : ""}</span>
                              <span className="text-gray-500 tabular-nums shrink-0">
                                {s.price ? `$${Number(s.price).toLocaleString()}` : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {l.saved_count > l.saved_listings.length && (
                          <div className="mt-2 text-[10px] text-gray-500">
                            +{l.saved_count - l.saved_listings.length} more not shown
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-4 text-xs text-gray-600">
                      {l.intent && <Row label="Intent" value={l.intent} />}
                      {l.timeline && <Row label="Timeline" value={l.timeline} />}
                      {(l.budget_min || l.budget_max) && (
                        <Row label="Budget" value={`$${Number(l.budget_min || 0).toLocaleString()} – $${Number(l.budget_max || 0).toLocaleString()}`} />
                      )}
                      {l.beds && <Row label="Beds/Baths" value={`${l.beds} / ${l.baths || "?"}`} />}
                      {l.property_type && <Row label="Type" value={l.property_type} />}
                      {l.financing_status && <Row label="Financing" value={l.financing_status} />}
                      {l.program_interest && <Row label="Program" value={l.program_interest} />}
                      {l.credit_range && <Row label="Credit" value={l.credit_range} />}
                    </div>

                    {l.neighborhoods?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {l.neighborhoods.map((n) => (
                          <span key={n} className="text-[10px] px-2 py-1 rounded-full bg-stone-100 text-[#1A1A1A]">{n}</span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="text-[#E63946] hover:text-[#c9313d] flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" /> Reply
                          </a>
                        )}
                        <button onClick={() => openTranscript(l)} className="text-gray-600 hover:text-[#1A1A1A] flex items-center gap-1" data-testid={`admin-lead-transcript-${l.id}`}>
                          <MessageSquare className="w-3.5 h-3.5" /> Transcript
                        </button>
                        <button onClick={() => deleteLead(l)} className="text-gray-400 hover:text-red-600 flex items-center gap-1" data-testid={`admin-lead-delete-${l.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                      <span className="text-gray-500">{new Date(l.updated_at).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
              {leads.length === 0 && (
                <div className="col-span-full text-center text-gray-500 py-12 bg-white border border-dashed border-gray-200 rounded-xl">
                  <Sparkles className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                  No leads yet. Once visitors chat with Ava, qualified leads will appear here.
                </div>
              )}
            </div>
          </>
        )}
        {tab === "loan_apps" && (
          <>
            <h2 className="font-display text-2xl font-semibold mb-6">Mortgage pre-approval applications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {loanApps.map((q) => (
                <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-lg font-semibold">{q.name}</div>
                      <div className="text-sm text-gray-500">{q.email} · {q.phone}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[#1E3A6D]/10 text-[#1E3A6D]">
                      {q.loan_type}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 text-xs text-gray-600">
                    {q.program && <Row label="Program" value={q.program} />}
                    {q.purchase_price ? <Row label="Price" value={`$${Number(q.purchase_price).toLocaleString()}`} /> : null}
                    {q.down_payment ? <Row label="Down" value={`$${Number(q.down_payment).toLocaleString()}`} /> : null}
                    {q.annual_income ? <Row label="Income" value={`$${Number(q.annual_income).toLocaleString()}`} /> : null}
                    {q.credit_range && <Row label="Credit" value={q.credit_range} />}
                    {q.timeline && <Row label="Timeline" value={q.timeline} />}
                  </div>
                  {q.message && (
                    <p className="mt-4 text-sm text-[#333] leading-relaxed whitespace-pre-line">{q.message}</p>
                  )}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <a href={`mailto:${q.email}`} className="hover:text-[#E63946] flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> Reply
                    </a>
                    <span>{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
              {loanApps.length === 0 && (
                <div className="col-span-full text-center text-gray-500 py-12">No loan applications yet.</div>
              )}
            </div>
          </>
        )}
      </div>

      {formOpen && (
        <PropertyForm
          initial={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={onSave}
        />
      )}

      {/* Transcript modal */}
      {transcriptOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setTranscriptOpen(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="font-display text-lg font-semibold">
                  {transcriptOpen.lead.name || "Anonymous visitor"}
                </div>
                <div className="text-xs text-gray-500">{transcriptOpen.lead.session_id}</div>
              </div>
              <button onClick={() => setTranscriptOpen(null)} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-stone-50">
              {transcriptOpen.messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#E63946] text-white" : "bg-white border border-gray-200 text-[#1A1A1A]"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {transcriptOpen.messages.length === 0 && (
                <div className="text-center text-gray-500 py-12 text-sm">No messages yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-400">{label}</span>
      <span className="text-[#1A1A1A] font-medium text-right">{value}</span>
    </div>
  );
}
